import IParser from "@/types/IParser"
import ParseError from "@/types/ParseError"
import dayjs from "dayjs"
import { TariffAgreements } from "./tariffAgreements"
import { ITariffAgreements } from "./tariffAgreements.types"
import { Agreement } from "@/models/agreement/agreement"
import { IAgreement } from "@/models/agreement/agreement.types"
import { Tariff } from "@/models/tariff/tariff"
import { IProductDetails, IUnitRates } from "@/models/tariff/tariff.types"
import {
  getBundleGroupCode,
  parseTariffRates,
} from "@/models/tariff/tariff.parser"
import tariffHelper from "@/helpers/tariffHelper"
import {
  TariffAgreements as TariffAgreementsResponse,
  TariffAgreementDetails,
  TariffDetails,
} from "@/api/backofficeRest/generated/model"

export type TariffAgreementsRaw = TariffAgreementsResponse

// Junifer's sentinel for an open-ended agreement. The Agreement model
// normalises it to a null endDate, so a missing backoffice toDate lands on the
// same open-ended semantics the Ark endpoint had.
const OPEN_ENDED_TO_DATE = "9999-12-31"

// The backoffice rates are {name, rateInPence}; parseTariffRates works on the
// Hasura {rate_name, rate_value} shape. Adapt rather than duplicate, so both
// sources capitalise and keep every rate name identically.
//
// flatMap rather than filter+map: codegen types the list items as
// Maybe<TariffRate>, and only the conditional-array form narrows both the item
// and its fields without a cast.
const parseRates = (
  tariffRates: TariffDetails["tariffRates"],
): IUnitRates | undefined =>
  parseTariffRates(
    tariffRates?.flatMap((rate) =>
      rate?.name != null && rate.rateInPence != null
        ? [{ rate_name: rate.name, rate_value: rate.rateInPence }]
        : [],
    ),
  )

// Standing charge and exit fee are passed through at full precision. They feed
// tariffHelper's annual-cost arithmetic and the electricity VAT strip, both of
// which divide; the display layer rounds via formatPriceInPence / formatMoney.
const parseProductDetails = (data: TariffDetails): IProductDetails => {
  const isGas = tariffHelper.isGas(data.code)
  const rates = parseRates(data.tariffRates)

  return {
    name: data.internalName,
    fromDttm: dayjs(data.productFromDttm),
    unitRate: isGas ? rates?.Standard : undefined,
    unitRates: isGas ? undefined : rates,
    standingCharge: data.standingChargeInPence,
    earlyTerminationCharge: data.earlyTerminationChargeInPounds,
    tariffComparisonCode: getBundleGroupCode(
      data.code,
      data.bundleDfnGroupCode,
    ),
    tariffComparisonRateName: data.bundleDfTypeName,
    vatRate: data.vatRate ?? undefined,
  }
}

// Must be a Tariff instance: isVariable, isOneRate, isGas and bundleCode are
// derived getters on the model, not payload fields.
const parseTariff = (data: TariffDetails): Tariff =>
  new Tariff({
    id: Number(data.id),
    code: data.code,
    gsp: data.gspGroup,
    display: data.displayName,
    productDetails: parseProductDetails(data),
    // Not in the payload, and only rendered in tariffQuoter off another source.
    isPrepay: false,
    isSmartPrepay: data.isSmartPrepay,
    isAvailableToPublic: data.isAvailableToPublic,
    directDebit: data.requiresDirectDebit,
    // We send 0 for variable tariffs where Ark sent null. Both are falsy to the
    // checks in renewals.ts, but map it so the model matches Ark exactly.
    durationMonths: data.durationInMonths || null,
    // variantFromDt is the established fe-nova convention for this field, as in
    // HasuraTariffParser. Ark's created_at was its own row-insertion time.
    createdAt: dayjs(data.variantFromDt),
  })

const parseActiveAgreement = (data: TariffAgreementDetails): IAgreement =>
  new Agreement({
    id: Number(data.id),
    startDate: data.fromDate,
    endDate: data.toDate ?? OPEN_ENDED_TO_DATE,
    orderedDate: data.orderedDate ?? null,
    isCancelled: data.isCancelled,
    productName: data.displayName,
    productCode: data.code,
    juniferNumber: data.juniferAgreementNumber,
  })

export const BackofficeTariffAgreementsParser: IParser<
  ITariffAgreements,
  TariffAgreementsRaw
> = {
  toDomain: (data: TariffAgreementsRaw) => {
    try {
      return new TariffAgreements({
        id: Number(data.meterPointId),
        activeAgreement: data.activeAgreement
          ? parseActiveAgreement(data.activeAgreement)
          : undefined,
        currentTariff: data.currentTariff
          ? parseTariff(data.currentTariff)
          : undefined,
        // Same Maybe<> widening as the rates: the schema says the items are
        // non-null, the generated type says Maybe<TariffDetails>.
        publiclyAvailableTariffList: data.publiclyAvailableTariffs?.flatMap(
          (tariff) => (tariff ? [parseTariff(tariff)] : []),
        ),
        smartPaygTariffs: data.smartPaygTariffs?.flatMap((tariff) =>
          tariff ? [parseTariff(tariff)] : [],
        ),
      })
    } catch (error) {
      throw new ParseError(error)
    }
  },
}
